import React, { useState } from 'react';
import { Calculator, Shield, TrendingUp, AlertCircle, Info } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PositionSizer — recommended lot size for a signal
 * User inputs account equity + risk %, gets broker-ready lot size
 */
const PositionSizer = ({ pair = 'XAU/USD', slPips = 20, tpPips = 40 }) => {
  const [equity, setEquity] = useState(10000);
  const [riskPct, setRiskPct] = useState(1.0);
  const [useKelly, setUseKelly] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const compute = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('fira_auth_token');
      const res = await fetch(`${API}/api/premium/position-size`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          account_equity_usd: Number(equity),
          sl_pips: Number(slPips),
          tp_pips: Number(tpPips),
          pair,
          risk_percent: Number(riskPct),
          use_kelly: useKelly,
        }),
      });
      const data = await res.json();
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
      }
    } catch (e) {
      setError(e.message || 'Failed to compute');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="position-sizer" style={{
      background: '#FFFFFF',
      borderRadius: '16px',
      padding: '18px',
      border: '1px solid rgba(99, 102, 241, 0.15)',
      fontFamily: 'Inter, sans-serif',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
        <Calculator size={16} color="#6366F1" />
        <span style={{ fontSize: '13px', fontWeight: '700', color: '#6366F1', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Position Size Advisor
        </span>
      </div>

      {/* Inputs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' }}>
        <div>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px' }}>
            Account Equity (USD)
          </label>
          <input
            data-testid="sizer-equity-input"
            type="number"
            value={equity}
            onChange={(e) => setEquity(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid #E2E8F0',
              fontSize: '14px',
              color: '#0F172A',
              background: '#F8FAFC',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748B', display: 'block', marginBottom: '4px' }}>
            Risk % per Trade
          </label>
          <input
            data-testid="sizer-risk-input"
            type="number"
            step="0.1"
            min="0.1"
            max="5"
            value={riskPct}
            onChange={(e) => setRiskPct(e.target.value)}
            disabled={useKelly}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '10px',
              border: '1px solid #E2E8F0',
              fontSize: '14px',
              color: useKelly ? '#94A3B8' : '#0F172A',
              background: useKelly ? '#F1F5F9' : '#F8FAFC',
              boxSizing: 'border-box',
            }}
          />
        </div>
      </div>

      {/* Kelly Toggle */}
      <label style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        fontSize: '12px', color: '#475569', marginBottom: '12px', cursor: 'pointer',
      }}>
        <input
          data-testid="sizer-kelly-toggle"
          type="checkbox"
          checked={useKelly}
          onChange={(e) => setUseKelly(e.target.checked)}
          style={{ margin: 0 }}
        />
        <span>Use Kelly Criterion <span style={{ color: '#94A3B8' }}>(quarter Kelly, capped 2%)</span></span>
      </label>

      {/* Compute Button */}
      <button
        data-testid="sizer-compute-btn"
        onClick={compute}
        disabled={loading}
        style={{
          width: '100%',
          padding: '11px',
          borderRadius: '10px',
          border: 'none',
          background: loading ? '#CBD5E1' : 'linear-gradient(135deg, #6366F1, #8B5CF6)',
          color: '#FFFFFF',
          fontSize: '13px',
          fontWeight: '700',
          cursor: loading ? 'wait' : 'pointer',
          letterSpacing: '0.02em',
        }}
      >
        {loading ? 'Calculating...' : 'Calculate Lot Size'}
      </button>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: '10px', padding: '8px 12px',
          background: 'rgba(239, 68, 68, 0.08)',
          borderRadius: '8px', fontSize: '12px', color: '#DC2626',
          display: 'flex', alignItems: 'center', gap: '6px',
        }}>
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Result */}
      {result && (
        <div data-testid="sizer-result" style={{ marginTop: '14px', padding: '14px', background: '#F8FAFC', borderRadius: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '10px' }}>
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>
                Recommended Lot Size
              </div>
              <div data-testid="sizer-result-lot" style={{ fontSize: '22px', fontWeight: '700', color: '#6366F1', fontFamily: 'Poppins, sans-serif' }}>
                {Number(result.lot_size).toFixed(2)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>
                Max Loss (USD)
              </div>
              <div data-testid="sizer-result-risk" style={{ fontSize: '22px', fontWeight: '700', color: '#EF4444', fontFamily: 'Poppins, sans-serif' }}>
                ${Number(result.risk_dollars).toFixed(2)}
              </div>
            </div>
          </div>

          <div style={{ fontSize: '11px', color: '#64748B', lineHeight: '1.5', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <div>
              <Shield size={10} style={{ display: 'inline', marginRight: '4px' }} />
              Pip value: ${result.pip_value_per_lot}/lot × {result.sl_pips} pips
            </div>
            <div>
              <TrendingUp size={10} style={{ display: 'inline', marginRight: '4px' }} />
              Actual account risk: {result.account_risk_percent}%
            </div>
            {result.formula === 'fractional_kelly' && (
              <div style={{ color: '#6366F1' }}>
                <Info size={10} style={{ display: 'inline', marginRight: '4px' }} />
                Kelly full: {(result.kelly_full_fraction * 100).toFixed(0)}% → used quarter: {result.recommended_risk_percent}%
              </div>
            )}
            {result.notes !== 'OK' && (
              <div style={{ color: '#F59E0B', marginTop: '4px' }}>
                <AlertCircle size={10} style={{ display: 'inline', marginRight: '4px' }} />
                {result.notes}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PositionSizer;
